import { useRef, useState } from 'react'
import { Alert, Button, Popconfirm, Space } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import { cfdApi } from '../services/api'
import { canResume, newRequestId, readSubmissions, rememberSubmissions } from '../utils/cfdCommands'
import type { AccountAuth } from '../types/cfdWorkspace'
import type { TradingOperation } from '../types/operations'

export default function CfdOperationRecovery({ tokenId, authMode, operation }: {
  tokenId: number; authMode: AccountAuth; operation: TradingOperation
}) {
  const client = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const inFlight = useRef(false)
  const alreadyResumed = readSubmissions(tokenId).some(item => item.originalId === operation.request_id)
  const act = async (resume: boolean) => {
    if (inFlight.current) return
    inFlight.current = true; setBusy(true); setError('')
    try {
      if (resume) {
        const requestId = newRequestId()
        rememberSubmissions(tokenId, [{ requestId, originalId: operation.request_id, createdAt: new Date().toISOString() }])
        await cfdApi(authMode).resume(tokenId, operation.request_id, requestId)
      } else await cfdApi(authMode).reconcile(tokenId, operation.request_id)
    } catch (e: any) { setError(e?.response?.data?.detail || '未取得查询结果，请查原请求 ID。不会自动重发交易。') }
    finally {
      await Promise.all(['token-operations', 'cfd-workspace', 'cfd-local-submissions'].map(key => client.invalidateQueries({ queryKey: [key, tokenId] })))
      inFlight.current = false; setBusy(false)
    }
  }
  return <Space direction="vertical" style={{ width: '100%' }}>
    {['unknown', 'applied_unverified', 'running', 'partial_success'].includes(operation.status) && <Alert type="info" showIcon message="先核实原操作" description="只读核验不会再次减仓或开仓；保护 API 回执尚不能证明券商已生效。" />}
    <Space wrap>
      <Button loading={busy} onClick={() => void act(false)}>只读核验原操作</Button>
      {canResume(operation) && <Popconfirm title="只恢复原计划中明确未执行的子腿" description="已完成子腿保持不动，仍使用原固定数量与保护目标。" onConfirm={() => act(true)}>
        <Button disabled={busy || alreadyResumed}>恢复明确失败子腿</Button>
      </Popconfirm>}
    </Space>
    {alreadyResumed && <Alert type="info" message="已创建恢复请求，请在操作记录查看其结果。" />}
    {!!error && <Alert type="warning" message={error} />}
  </Space>
}
